import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { formatPhone } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { RecordLink } from '../components/RecordLink.js';
import { Pager } from '../components/Pager.js';

/**
 * Incoming requests — the calls still waiting on a person.
 *
 * Almost every recorded inbound call in the history is a voicemail: one voice, giving a name,
 * a number, an address and a problem. Those are requests for work, and until now they landed
 * in the same undifferentiated log as calls somebody had already handled, which meant the only
 * way to find an unanswered one was to read the list and remember.
 *
 * The screen is built around the single question somebody has when they open it — *who is
 * still waiting, and what do I need to call them back about* — so the list leads with the
 * caller and what they wanted, not with a timestamp and a phone number. Two panes: the queue,
 * and the call. Which call is open lives in the URL, so it can be linked to and survives a
 * reload.
 *
 * The one thing this screen is careful about is the difference between what we know and what
 * we were told. A name transcribed from somebody spelling it into a machine is a claim, and it
 * is labelled as one everywhere it appears. Confirming it is a person's decision, taken by
 * pressing a button — never something the screen quietly does on their behalf.
 */

type Reported = {
  name: string | null;
  phone: string | null;
  email: string | null;
  address: Record<string, string> | null;
  dateDiscussed: string | null;
};

type Item = {
  id: string;
  startedAt: string;
  durationSeconds: number;
  outcome: string;
  fromNumber: string;
  reason: string | null;
  servicesRequested: string | null;
  sentiment: string | null;
  confidence: number | null;
  reported: Reported;
  summary: string | null;
  language: string | null;
  hasRecording: boolean;
  hasTranscript: boolean;
  customer: { id: string; name: string | null } | null;
  customerConfirmed: boolean;
  resolvedAt: string | null;
  resolution: string | null;
  note: string | null;
  resolvedByName: string | null;
};

type Queue = {
  items: Item[];
  total: number;
  waiting: number;
  page: number;
  pageSize: number;
  pageCount: number;
};

type Detail = Item & {
  transcript: string | null;
  recordingUrl: string | null;
  candidates: Array<{ id: string; name: string; matchedOn: string }>;
};

const RESOLUTIONS = ['called_back', 'converted', 'not_needed', 'duplicate'] as const;

export function IntakeScreen() {
  const { t } = useTranslation();
  const search = useSearch({ from: '/intake' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const page = search.page ?? 1;
  const pageSize = search.pageSize ?? 50;
  const state = search.state ?? 'open';
  const kind = search.kind ?? 'all';

  const setSearch = (patch: Record<string, unknown>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }), replace: true });

  const queue = useQuery({
    queryKey: ['intake', { state, kind, reason: search.reason, q: search.q, page, pageSize }],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ state, kind, page: String(page), limit: String(pageSize) });
      if (search.reason) params.set('reason', search.reason);
      if (search.q) params.set('q', search.q);
      return request<Queue>(`/intake?${params}`, { signal });
    },
    placeholderData: (prev) => prev,
  });

  const openId = search.call ?? queue.data?.items[0]?.id ?? null;

  const detail = useQuery({
    queryKey: ['intake', 'detail', openId],
    queryFn: ({ signal }) => request<Detail>(`/intake/${openId}`, { signal }),
    enabled: Boolean(openId),
  });

  // Every action refreshes both panes: resolving a call removes it from the open queue, and
  // the badge in the sidebar is read from the same response.
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['intake'] });

  const resolve = useMutation({
    mutationFn: ({ id, resolution, note }: { id: string; resolution: string; note?: string }) =>
      request<void>(`/intake/${id}/resolve`, { method: 'POST', body: JSON.stringify({ resolution, note }) }),
    onSuccess: refresh,
  });

  const reopen = useMutation({
    mutationFn: (id: string) => request<void>(`/intake/${id}/reopen`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const link = useMutation({
    mutationFn: ({ id, customerId }: { id: string; customerId: string }) =>
      request<void>(`/intake/${id}/link`, { method: 'POST', body: JSON.stringify({ customerId }) }),
    onSuccess: refresh,
  });

  const data = queue.data;

  return (
    <div className="screen intake">
      <header className="intake__head">
        <div>
          <h1 className="intake__title">{t('intake.title')}</h1>
          <p className="intake__lede">{t('intake.lede')}</p>
        </div>
        {data && <span className="intake__count">{t('intake.waiting', { count: data.waiting })}</span>}
      </header>

      <div className="intake__filters" role="group" aria-label={t('intake.title')}>
        <Segmented
          value={state}
          onChange={(v) => setSearch({ state: v === 'open' ? undefined : v, page: undefined, call: undefined })}
          options={[
            { value: 'open', label: t('intake.state.open') },
            { value: 'handled', label: t('intake.state.done') },
            { value: 'all', label: t('common.all', { defaultValue: 'Tout' }) },
          ]}
        />
        <Segmented
          value={kind}
          onChange={(v) => setSearch({ kind: v === 'all' ? undefined : v, page: undefined, call: undefined })}
          options={[
            { value: 'all', label: t('common.all', { defaultValue: 'Tout' }) },
            { value: 'voicemail', label: t('call.outcome.voicemail') },
            { value: 'missed', label: t('call.outcome.missed') },
            { value: 'unmatched', label: t('intake.unmatched') },
          ]}
        />
        <input
          className="intake__search"
          type="search"
          value={search.q ?? ''}
          placeholder={t('common.searchPlaceholder')}
          onChange={(e) => setSearch({ q: e.target.value || undefined, page: undefined })}
        />
      </div>

      {queue.isError && <div className="notice notice--error">{t('errors.generic')}</div>}

      {data && data.items.length === 0 && (
        <div className="empty">
          <span className="empty__title">{t('intake.empty.title')}</span>
          <p className="empty__body">{t('intake.empty.body')}</p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="intake__panes">
          <ol className="intake__list">
            {data.items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`intake__row${item.id === openId ? ' intake__row--open' : ''}`}
                  aria-current={item.id === openId}
                  onClick={() => setSearch({ call: item.id })}
                >
                  <span className={`intake__badge intake__badge--${item.outcome}`}>
                    {t(`call.outcome.${item.outcome}`)}
                  </span>
                  <span className="intake__who">
                    {/*
                      The caller's own account of who they are comes first when we cannot place
                      the number, because it is the only name anybody has to go on. A confirmed
                      customer always outranks it.
                    */}
                    {item.customer ? (
                      <strong>{item.customer.name}</strong>
                    ) : item.reported.name ? (
                      <span className="intake__claimed" title={t('call.reportedBy')}>
                        {item.reported.name}
                      </span>
                    ) : (
                      <span className="intake__anon">{t('intake.unmatched')}</span>
                    )}
                    <span className="tnum intake__phone">{formatPhone(item.fromNumber)}</span>
                  </span>
                  <span className="intake__want">
                    {item.servicesRequested ?? item.summary ?? '—'}
                  </span>
                  {item.reason && <span className="intake__reason">{t(`call.reason.${item.reason}`)}</span>}
                  <span className="tnum intake__when">{when(item.startedAt)}</span>
                  {item.resolvedAt && (
                    <span className="intake__resolved">{t(`intake.resolution.${item.resolution}`)}</span>
                  )}
                </button>
              </li>
            ))}
          </ol>

          <section className="intake__detail" aria-live="polite">
            {detail.isLoading && <p className="screen__loading">{t('common.loading')}</p>}
            {detail.data && (
              <CallDetail
                call={detail.data}
                onResolve={(resolution, note) => resolve.mutate({ id: detail.data!.id, resolution, note })}
                onReopen={() => reopen.mutate(detail.data!.id)}
                onLink={(customerId) => link.mutate({ id: detail.data!.id, customerId })}
                busy={resolve.isPending || reopen.isPending || link.isPending}
              />
            )}
          </section>
        </div>
      )}

      {data && (
        <Pager
          page={data.page}
          pageCount={data.pageCount}
          pageSize={data.pageSize}
          total={data.total}
          count={data.items.length}
          label={t('intake.title')}
          busy={queue.isFetching}
          onPage={(next: number) => setSearch({ page: next === 1 ? undefined : next })}
          onPageSize={(size: number) => setSearch({ pageSize: size === 50 ? undefined : size, page: undefined })}
        />
      )}
    </div>
  );
}

function CallDetail({
  call,
  onResolve,
  onReopen,
  onLink,
  busy,
}: {
  call: Detail;
  onResolve: (resolution: string, note?: string) => void;
  onReopen: () => void;
  onLink: (customerId: string) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [showTranscript, setShowTranscript] = useState(false);

  return (
    <>
      <header className="calldetail__head">
        <div>
          <h2>
            {call.customer ? (
              <RecordLink kind="customer" id={call.customer.id}>
                {call.customer.name}
              </RecordLink>
            ) : (
              call.reported.name ?? t('intake.unmatched')
            )}
          </h2>
          <p className="calldetail__meta tnum">
            {formatPhone(call.fromNumber)} · {when(call.startedAt)} · {duration(call.durationSeconds)}
          </p>
        </div>
        <span className={`intake__badge intake__badge--${call.outcome}`}>{t(`call.outcome.${call.outcome}`)}</span>
      </header>

      {/*
        The recording, when there is one. Given controls rather than autoplay: somebody may be
        on another call, and audio that starts by itself is the fastest way to make a screen
        unusable in a shared office.
      */}
      {call.recordingUrl && (
        <audio className="calldetail__audio" controls preload="none" src={call.recordingUrl}>
          {t('intake.action.listen')}
        </audio>
      )}

      {call.summary && (
        <section className="calldetail__block">
          <h3>{t('intake.summary')}</h3>
          <p>{call.summary}</p>
        </section>
      )}

      {(call.reported.name || call.reported.phone || call.reported.email || call.reported.address) && (
        <section className="calldetail__block calldetail__block--claimed">
          {/*
            Labelled, always. These came out of a transcription of somebody reciting their own
            details to a machine — useful for calling back, and not a record of anything until
            a person says it is.
          */}
          <h3>
            {t('call.reportedBy')}
            {call.confidence !== null && call.confidence < 60 && (
              <span className="calldetail__warn"> · {t('call.lowConfidence')}</span>
            )}
          </h3>
          <dl className="calldetail__facts">
            {call.reported.name && <Fact label={t('customer.name', { defaultValue: 'Nom' })} value={call.reported.name} />}
            {call.reported.phone && <Fact label={t('message.transport.sms')} value={formatPhone(call.reported.phone)} />}
            {call.reported.email && <Fact label={t('message.transport.email')} value={call.reported.email} />}
            {call.reported.address && (
              <Fact label={t('customer.address', { defaultValue: 'Adresse' })} value={addressLine(call.reported.address)} />
            )}
            {call.servicesRequested && (
              <Fact label={t('intake.summary')} value={call.servicesRequested} />
            )}
            {call.reported.dateDiscussed && <Fact label={t('common.today')} value={call.reported.dateDiscussed} />}
          </dl>
        </section>
      )}

      {call.transcript && (
        <section className="calldetail__block">
          <button type="button" className="calldetail__toggle" onClick={() => setShowTranscript((v) => !v)}>
            {t('intake.transcript')}
          </button>
          {showTranscript && <p className="calldetail__transcript">{call.transcript}</p>}
        </section>
      )}

      {/* Who this might be — suggestions, never applied on their own. */}
      {!call.customer && call.candidates.length > 0 && (
        <section className="calldetail__block">
          <h3>{t('intake.action.linkCustomer')}</h3>
          <ul className="calldetail__candidates">
            {call.candidates.map((c) => (
              <li key={c.id}>
                <button type="button" disabled={busy} onClick={() => onLink(c.id)}>
                  {c.name}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <footer className="calldetail__actions">
        {call.resolvedAt ? (
          <>
            <p className="calldetail__done">
              {t(`intake.resolution.${call.resolution}`)}
              {call.resolvedByName && ` · ${t('intake.handledBy', { name: call.resolvedByName })}`}
            </p>
            {call.note && <p className="calldetail__note">{call.note}</p>}
            <button type="button" className="btn" disabled={busy} onClick={onReopen}>
              {t('intake.action.reopen')}
            </button>
          </>
        ) : (
          <>
            <label className="calldetail__notefield">
              <span>{t('intake.note')}</span>
              <input
                type="text"
                value={note}
                placeholder={t('intake.notePlaceholder')}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            <div className="calldetail__buttons">
              {RESOLUTIONS.map((resolution) => (
                <button
                  key={resolution}
                  type="button"
                  className={`btn${resolution === 'called_back' ? ' btn--primary' : ''}`}
                  disabled={busy}
                  onClick={() => onResolve(resolution, note || undefined)}
                >
                  {t(`intake.resolution.${resolution}`)}
                </button>
              ))}
            </div>
          </>
        )}
      </footer>
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="calldetail__fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function Segmented({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="segmented" role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          className={`segmented__option${option.value === value ? ' segmented__option--on' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** The parts the caller actually gave, in the order somebody would read them aloud. */
function addressLine(address: Record<string, string>): string {
  return (
    address.fullAddress ??
    [[address.streetNumber, address.streetName].filter(Boolean).join(' '), address.city, address.postalCode]
      .filter(Boolean)
      .join(', ')
  );
}

function duration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function when(iso: string): string {
  const at = new Date(iso);
  const sameDay = at.toDateString() === new Date().toDateString();
  return sameDay
    ? at.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })
    : at.toLocaleString('fr-CA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
